/**
 * Picos de la evolución de la espera o la duración. Funciones puras, sin React: se calculan en el front con los puntos que
 * ya llegaron (un punto por día, semana o mes).
 */

/** Un período con menos atenciones que esto (o que la cuarta parte de la mediana, si es más) no se marca nunca como pico. */
export const PEAK_MIN_ATTENTIONS = 10;
export const PEAK_MEDIAN_SHARE = 0.25;
/** Sin al menos esta cantidad de períodos que cuentan no hay con qué comparar: no se marca ninguno. */
export const PEAK_MIN_COUNTING_POINTS = 7;
/** Se marcan, como mucho, los más altos. */
export const PEAK_MAX_MARKED = 3;
/** Un pico es un valor de al menos la media más tantos desvíos. */
export const PEAK_DEVIATIONS = 2;

export interface PeakInput {
    /** El valor de la medida en el período (espera o duración media); null si no hubo atenciones. */
    value: number | null;
    attentions: number;
}

export interface PeakAnalysis {
    /** Atenciones mínimas para que un período cuente: el criterio que se le muestra al usuario. */
    minAttentions: number;
    /** Por posición: el período tiene valor y atenciones suficientes. */
    counting: boolean[];
    /** Por posición: tiene atenciones, pero menos que `minAttentions` (su promedio puede no ser representativo). */
    lowVolume: boolean[];
    /** Posiciones de los picos, del más alto al más bajo. */
    peaks: number[];
    /** Media y desvío (poblacional) de los períodos que cuentan; null si no hay ninguno. */
    mean: number | null;
    deviation: number | null;
    /** Valor desde el cual un período es un pico; null si no se pudo calcular. */
    threshold: number | null;
}

/** Mediana de una lista de números (0 si está vacía). */
export function median(values: number[]): number {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 1 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

/**
 * Atenciones mínimas para que un período cuente: el mayor entre 10 y el 25 % de la mediana de atenciones por período
 * (se miran los períodos con atenciones), redondeado hacia arriba para poder decirlo como un número entero.
 */
export function minAttentionsFor(attentions: number[], floor: number = PEAK_MIN_ATTENTIONS): number {
    const withAttentions = attentions.filter(count => count > 0);
    return Math.ceil(Math.max(floor, PEAK_MEDIAN_SHARE * median(withAttentions)));
}

/**
 * Marca los picos de una serie. Solo cuentan los períodos con valor y con al menos `minAttentions` atenciones; hacen falta
 * `PEAK_MIN_COUNTING_POINTS` y se marcan los que llegan a la media + `PEAK_DEVIATIONS` desvíos, como mucho
 * `PEAK_MAX_MARKED`, los más altos. Sin variación (todos iguales) no hay picos. Quien la llama decide para qué medidas
 * sirve: con «Atenciones» el lunes alto y el domingo bajo son el patrón de la semana, no un pico.
 * `minFloor` es el piso de atenciones mínimas (por defecto `PEAK_MIN_ATTENTIONS`): una pantalla con otro volumen por
 * período (una guardia recibe cientos de pacientes por día) pasa el suyo.
 */
export function markPeaks(points: PeakInput[], minFloor: number = PEAK_MIN_ATTENTIONS): PeakAnalysis {
    const minAttentions = minAttentionsFor(points.map(point => point.attentions), minFloor);
    const counting = points.map(point => point.value !== null && point.attentions >= minAttentions);
    const lowVolume = points.map(point => point.value !== null && point.attentions > 0 && point.attentions < minAttentions);
    const values = points.flatMap((point, index) => (counting[index] && point.value !== null ? [point.value] : []));

    if (values.length === 0) {
        return { minAttentions, counting, lowVolume, peaks: [], mean: null, deviation: null, threshold: null };
    }
    const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
    const deviation = Math.sqrt(values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length);
    const threshold = mean + PEAK_DEVIATIONS * deviation;

    const peaks = values.length >= PEAK_MIN_COUNTING_POINTS && deviation > 0
        ? points.flatMap((point, index) => (counting[index] && point.value !== null && point.value >= threshold
            ? [{ index, value: point.value }] : []))
            .sort((a, b) => b.value - a.value || a.index - b.index)
            .slice(0, PEAK_MAX_MARKED)
            .map(item => item.index)
        : [];
    return { minAttentions, counting, lowVolume, peaks, mean, deviation, threshold };
}
